import { filter, isString } from 'lodash';
import { JSX } from 'react';

// eslint-disable-next-line import/no-cycle
import { Scope } from './scope';
import { RouteAttr } from './types';

export type ActionAttr = RouteAttr & {
  /**
   * type of action
   *
   * The default actions that are managed are: `index`, `new`, `edit`, `show`. Those names followed the REST definitions,
   * this means that some actions like `edit` require an `:id` argument.
   *
   * You can specify any type of action, by default they will not be considered a member
   */
  type: string;
  /** React element that this path will render */
  element: JSX.Element;
  /**
   * Alias name for a path, this allow you to keep the same type of names across the code but present of different one
   * to the end user.
   *
   * For example `/sessions/new` can be alias to `/login`
   */
  alias?: string;
  /**
   * Define if the path will require an `:id` or not
   *
   * If you put a customer type of action or would like to overwrite an default action
   */
  member?: boolean;
  /**
   * List of extra parameters that are added at the end of a generated URLs. The order of those params are very
   * important.
   *
   * This simplify the usage of `useParams` in React
   *
   * For example:
   * extraParams: `['groupId', 'authorId']`
   * will generate: 'my/:id/url/:groupId/:authorId
   */
  extraParams?: string[];
  /**
   * List of extra parameters that are added optionally at the end of a generated URLs. The order of those params are very
   * important.
   *
   * This simplify the usage of `useParams` in React
   */
  optionalExtraParams?: string[];
};

/**
 * Join argument together to create a URL path
 */
const join = (...paths: (string | undefined)[]): string => {
  const cleanedPaths = filter(paths, (p) => isString(p));

  return cleanedPaths.join('/').replace(/\/{2,}/g, '/');
};

/**
 * Inside the URL build the name of the parent param for nested actions
 *
 * This name will be singular, for example: `/authors/:authorId/books/new`
 */
const parentParam = (parent: Scope): string | undefined => {
  if (!parent.name) {
    return undefined;
  }
  if (parent.param) {
    return parent.param;
  }
  if (parent.name[parent.name.length - 1] === 's') {
    return `${parent.name.slice(0, -1)}Id`;
  }

  return `${parent.name}Id`;
};

/**
 * Build the path of the resources
 */
const buildResourcesPath = (object: Scope, action: Action | null = null): string => {
  let path = action?.alias || object.name || '';

  if (object.parent?.member && object.parent.name) {
    path = join(`/:${parentParam(object.parent)}`, path);
  }

  if (!object.parent) {
    return path;
  }

  return join(buildResourcesPath(object.parent), path);
};

/**
 * Add the `:` attribute in front to be able to be used as param
 */
const formatExtraParams = (action: Action): string | undefined => {
  return action.extraParams?.map((p) => `:${p}`).join('/');
};

/**
 * Add the `:?` attribute in front to be able to be used as param
 */
const formatOptionalExtraParams = (action: Action): string | undefined => {
  return action.optionalExtraParams?.map((p) => `:${p}?`).join('/');
};

const buildPath = (scope: Scope | undefined, action: Action): string | undefined => {
  const objetsPath: string | undefined = scope ? buildResourcesPath(scope, action) : undefined;

  if (action.alias) {
    return action.alias;
  }

  switch (action.type) {
    case 'index':
      return join(objetsPath || '', formatExtraParams(action), formatOptionalExtraParams(action));

    case 'new':
      return join(objetsPath, 'new', formatExtraParams(action), formatOptionalExtraParams(action));

    case 'edit':
      if (action.member === false) {
        return join(objetsPath, 'edit', formatExtraParams(action), formatOptionalExtraParams(action));
      }
      return join(objetsPath, `:${action.param}/edit`, formatExtraParams(action), formatOptionalExtraParams(action));

    case 'show':
      return join(objetsPath, `:${action.param}`, formatExtraParams(action), formatOptionalExtraParams(action));

    case 'default':
      return join(objetsPath, '*');

    default:
      if (!action.member) {
        return join(objetsPath, action.type, formatExtraParams(action), formatOptionalExtraParams(action));
      }

      return join(
        objetsPath,
        `:${action.param}/${action.type}`,
        formatExtraParams(action),
        formatOptionalExtraParams(action),
      );
  }
};

const buildComputedName = (object: Scope): string => {
  const path = object.name || '';

  if (!object.parent) {
    return path;
  }

  const parentComputed = buildComputedName(object.parent);
  if (parentComputed === '') {
    return path;
  }

  return `${parentComputed}_${path}`;
};

export class Action {
  public type: ActionAttr['type'];
  public alias?: ActionAttr['alias'];
  public element: ActionAttr['element'];
  public member: ActionAttr['member'];
  public param: ActionAttr['param'];
  public extraParams: ActionAttr['extraParams'];
  public optionalExtraParams: ActionAttr['optionalExtraParams'];
  public fullPath: string;
  public object: Scope;
  public computedName: string;

  constructor(attributes: ActionAttr, object: Scope) {
    this.type = attributes.type;
    this.alias = attributes.alias;
    this.element = attributes.element;
    this.member = attributes.member;
    this.extraParams = attributes.extraParams;
    this.optionalExtraParams = attributes.optionalExtraParams;
    this.object = object;
    this.param = attributes.param || 'id';
    this.fullPath = join('', buildPath(this.object, this));
    this.computedName = buildComputedName(this.object);
  }

  public details(): string {
    return `${this.computedName}#${this.type} -> ${this.fullPath}`;
  }
}
